/**
 * Agent Group Service
 * API client for agent grouping feature
 */

import api from './api';

export interface StepCondition {
  type: 'gate_status' | 'agent_approval' | 'custom';
  field?: string;
  operator: 'eq' | 'neq' | 'gt' | 'lt' | 'contains';
  value: string | number;
  description: string;
}

export interface TemplateBranch {
  branchId: string;
  label: string;
  condition: StepCondition | null;
  steps: TemplateStep[];
}

export interface TemplateStep {
  order: number;
  agentId: string;
  action: string;
  description: string;
  gate: string | null;
  stepId: string;
  parentStepId?: string;
  condition?: StepCondition | null;
  branches?: TemplateBranch[];
}

export interface AgentGroup {
  id: string;
  name: string;
  description: string;
  color: string;
  agentIds: string[];
  order: number;
  isCollapsed: boolean;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
}

/**
 * Fetch all agent groups
 */
export async function getAllAgentGroups(): Promise<AgentGroup[]> {
  const response = await api.get<AgentGroup[]>('/agent-groups');
  return response.data;
}

/**
 * Fetch a single agent group by ID
 */
export async function getAgentGroup(id: string): Promise<AgentGroup> {
  const response = await api.get<AgentGroup>(`/agent-groups/${id}`);
  return response.data;
}

/**
 * Create a new agent group
 */
export async function createAgentGroup(
  group: Omit<AgentGroup, 'id' | 'createdAt' | 'updatedAt'>
): Promise<AgentGroup> {
  const response = await api.post<AgentGroup>('/agent-groups', group);
  return response.data;
}

/**
 * Update an existing agent group
 */
export async function updateAgentGroup(
  id: string,
  updates: Partial<AgentGroup>
): Promise<AgentGroup> {
  const response = await api.put<AgentGroup>(`/agent-groups/${id}`, updates);
  return response.data;
}

/**
 * Delete an agent group
 */
export async function deleteAgentGroup(id: string): Promise<void> {
  await api.delete(`/agent-groups/${id}`);
}

/**
 * Update agent membership in a group
 */
export async function updateGroupAgents(
  groupId: string,
  agentIds: string[]
): Promise<AgentGroup> {
  const response = await api.put<AgentGroup>(`/agent-groups/${groupId}/agents`, {
    agentIds,
  });
  return response.data;
}
